// Scenario player — the heart of v2. A scenario is a sequence of
// reasoning steps that, when played, drive UI state across the
// graph, activity feed, and Why panel. Each step is a pure data
// object — strictly typed — that consumers map to highlights.

export type ScenarioKind =
  | "ingest"
  | "ruleFire"
  | "alert"
  | "memoryMatch"
  | "substitute"
  | "explain"
  | "budgetSlip"
  | "styleDrift"
  | "approvalAged"
  | "memoryPromote";

export interface ScenarioStep {
  id: string;
  kind: ScenarioKind;
  label: string;          // short, shown in the ticker
  detail: string;         // a sentence shown in the Why fragment
  highlightNodes: string[];
  highlightTab?: "graph" | "catalog" | "inspiration" | "rules" | "memory";
  focusNodeId?: string;   // sets the Why-engine focus
  durationMs: number;     // how long this step holds
  badge?: "warn" | "info" | "ok" | "money";
  dataset?: string;       // optional source citation
  delta?: string;         // monetary or time delta shown as a chip
}

export interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  outcome: string;        // headline outcome chip shown at end
  steps: ScenarioStep[];
}

// ─── SCENARIO 1 — lead-time risk → substitute ──────────────────────
const leadTimeRisk: Scenario = {
  id: "lead-time-risk",
  title: "A chandelier won't arrive in time — find a swap",
  subtitle: "A piece won't arrive in time. We propose a swap that fits the style.",
  outcome: "₹ 64,000 SKU swap · −78 days lead · 88% style match",
  steps: [
    {
      id: "s1-1",
      kind: "ingest",
      label: "SKU added to BOQ",
      detail: "Pendant chandelier · L-887 · 92-day lead · ₹64,000.",
      highlightNodes: ["s2"],
      highlightTab: "catalog",
      focusNodeId: "s2",
      durationMs: 1100,
      dataset: "Kaggle #6 e-commerce",
    },
    {
      id: "s1-2",
      kind: "ruleFire",
      label: "Rule R-lead-time fires",
      detail: "SKU.leadDays (92) > Milestone.dueIn (64) − 14 days slack.",
      highlightNodes: ["s2", "ru1"],
      highlightTab: "rules",
      focusNodeId: "ru1",
      durationMs: 1100,
      badge: "warn",
    },
    {
      id: "s1-3",
      kind: "alert",
      label: "Milestone at risk",
      detail: "Lighting install (18 Jul) blocked by long-lead SKU.",
      highlightNodes: ["s2", "ru1", "al1", "ms3"],
      highlightTab: "graph",
      focusNodeId: "al1",
      durationMs: 1100,
      badge: "warn",
    },
    {
      id: "s1-4",
      kind: "memoryMatch",
      label: "Memory mc1 matches",
      detail: "Studio pattern: substitute 90-day chandeliers — 7 hits, 1 reject.",
      highlightNodes: ["s2", "mc1"],
      highlightTab: "memory",
      focusNodeId: "mc1",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "s1-5",
      kind: "substitute",
      label: "Substitute proposed",
      detail: "Pendant L-901 · 14-day lead · 88% style match · ΔPrice +₹7,000.",
      highlightNodes: ["s2", "s2b", "mc1"],
      highlightTab: "catalog",
      focusNodeId: "s2b",
      durationMs: 1400,
      badge: "money",
      delta: "−78 days · 88%",
    },
  ],
};

// ─── SCENARIO 2 — budget cap drop by 12% ──────────────────────────
const budgetDrop: Scenario = {
  id: "budget-drop",
  title: "Client trimmed the budget — protect the rooms that matter",
  subtitle: "Two rooms breach their share. We rebalance without breaking the look.",
  outcome: "2 over-cap fixed · style cohesion drop 1.4%",
  steps: [
    {
      id: "s2-1",
      kind: "ingest",
      label: "Budget cap reset",
      detail: "Project.budgetCap 14,00,000 → 12,32,000 (−12%).",
      highlightNodes: ["p1"],
      highlightTab: "graph",
      focusNodeId: "p1",
      durationMs: 1000,
    },
    {
      id: "s2-2",
      kind: "budgetSlip",
      label: "BudgetLines breach",
      detail: "Kitchen modular (₹3.12 L) and Living furniture (₹1.78 L) exceed share targets.",
      highlightNodes: ["b3", "b1"],
      highlightTab: "graph",
      focusNodeId: "b3",
      durationMs: 1200,
      badge: "warn",
    },
    {
      id: "s2-3",
      kind: "ruleFire",
      label: "Rule R-budget-room-share fires",
      detail: "BudgetLine.share > 25% on Room=kitchen — flagged.",
      highlightNodes: ["b3", "ru2"],
      highlightTab: "rules",
      focusNodeId: "ru2",
      durationMs: 1000,
      badge: "warn",
    },
    {
      id: "s2-4",
      kind: "memoryMatch",
      label: "Memory mc2 matches",
      detail: "Kitchen budget rebalance pattern — 12 hits, 0 rejects.",
      highlightNodes: ["b3", "mc1"],
      highlightTab: "memory",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "s2-5",
      kind: "substitute",
      label: "Redistribution suggested",
      detail: "Move ₹40k from Bedroom storage → Kitchen counter; style cohesion ↓ 1.4 pp.",
      highlightNodes: ["b3", "b4"],
      highlightTab: "graph",
      durationMs: 1400,
      badge: "money",
      delta: "fits cap · cohesion −1.4 pp",
    },
  ],
};

// ─── SCENARIO 3 — style drift on a Placement ─────────────────────
const styleDrift: Scenario = {
  id: "style-drift",
  title: "One piece looks off — catch it before you render",
  subtitle: "One piece doesn't match the look. We catch it before the render runs.",
  outcome: "$ 75 render avoided · style mismatch caught",
  steps: [
    {
      id: "s3-1",
      kind: "ingest",
      label: "Placement updated",
      detail: "Bunk bed K-220 placed in kids room · style 0.79.",
      highlightNodes: ["s4"],
      highlightTab: "catalog",
      focusNodeId: "s4",
      durationMs: 1000,
    },
    {
      id: "s3-2",
      kind: "styleDrift",
      label: "Style cosine drops",
      detail: "0.79 < project Intent threshold 0.82.",
      highlightNodes: ["s4", "it1"],
      highlightTab: "inspiration",
      focusNodeId: "it1",
      durationMs: 1100,
      badge: "warn",
    },
    {
      id: "s3-3",
      kind: "alert",
      label: "Pre-render flag",
      detail: "Restyle suggestion queued before Take Render is called.",
      highlightNodes: ["s4"],
      highlightTab: "rules",
      durationMs: 1100,
      badge: "money",
      delta: "$25 render avoided",
    },
  ],
};

// ─── SCENARIO 4 — RERA approval aging ────────────────────────────
const reraAging: Scenario = {
  id: "rera-aging",
  title: "An approval has gone quiet — give it a nudge",
  subtitle: "An approval has been sitting too long. Handover at risk — we draft a refresh.",
  outcome: "−18 days handover slip avoided",
  steps: [
    {
      id: "s4-1",
      kind: "ingest",
      label: "Approval aged",
      detail: "RERA filing GJ-2023-441 · approved 92 days ago.",
      highlightNodes: ["a3"],
      highlightTab: "graph",
      focusNodeId: "a3",
      durationMs: 1000,
      dataset: "Kaggle #8 Gujarat RERA",
    },
    {
      id: "s4-2",
      kind: "approvalAged",
      label: "Risk computed",
      detail: "Handover milestone (30 Aug) dependent on approval freshness.",
      highlightNodes: ["a3", "ms4"],
      highlightTab: "graph",
      durationMs: 1100,
      badge: "warn",
    },
    {
      id: "s4-3",
      kind: "memoryMatch",
      label: "Memory mc5 matches",
      detail: "RERA filing packet auto-template — 6 hits, 0 rejects.",
      highlightNodes: ["a3"],
      highlightTab: "memory",
      durationMs: 1100,
      badge: "ok",
      delta: "−18 days slip avoided",
    },
  ],
};

// ─── SCENARIO 5 — memory promotion ────────────────────────────────
const memoryPromote: Scenario = {
  id: "memory-promote",
  title: "A favourite habit becomes a studio rule",
  subtitle: "A rule that's worked many times turns into a studio default.",
  outcome: "+1 reusable Memory Card in studio library",
  steps: [
    {
      id: "s5-1",
      kind: "ruleFire",
      label: "Rule R-lead-time accept #5",
      detail: "5 accepts · 0 rejects · accept ratio 100%.",
      highlightNodes: ["ru1"],
      highlightTab: "rules",
      focusNodeId: "ru1",
      durationMs: 1100,
      badge: "ok",
    },
    {
      id: "s5-2",
      kind: "memoryPromote",
      label: "Promotion threshold met",
      detail: "≥ 5 accepts AND reject-ratio < 15% → promote to studio.",
      highlightNodes: ["ru1", "mc1"],
      highlightTab: "memory",
      focusNodeId: "mc1",
      durationMs: 1200,
      badge: "money",
      delta: "+1 Memory Card",
    },
    {
      id: "s5-3",
      kind: "memoryMatch",
      label: "Available on future projects",
      detail: "Memory Card mc1 now matches every new SKU with leadDays > 60.",
      highlightNodes: ["mc1"],
      highlightTab: "memory",
      durationMs: 1100,
      badge: "ok",
    },
  ],
};

/* Scenarios 6–9 carry only the human-readable title/subtitle/outcome
   here — the step bodies are produced at click time by the live graph
   generators in `scenarioGenerators.ts`. CLAUDE.md §1: never literal
   prices, lead-days, or vendor names in code. */
const emailDraft: Scenario = {
  id: "email-draft",
  title: "Draft the client about the swap",
  subtitle: "Self-Refine: agent drafts, critiques, revises — then waits for your nod.",
  outcome: "Draft cites graph nodes · 0 auto-sends",
  steps: [],
};
const sprintReschedule: Scenario = {
  id: "sprint-reschedule",
  title: "Reschedule the sprint around the swap",
  subtitle: "OR-Tools CP-SAT solves the new critical path in milliseconds.",
  outcome: "Plan recomputed · critical path shifts · slack restored",
  steps: [],
};
const estimateUpdate: Scenario = {
  id: "estimate-update",
  title: "Update the estimate after the swap",
  subtitle: "Decimal-safe math · every line cited · change-order saga drafts itself.",
  outcome: "Estimate v2 + Change Order · 0 auto-sends",
  steps: [],
};
const siteVisit: Scenario = {
  id: "site-visit",
  title: "Book a site visit before the next milestone",
  subtitle: "Calendar intersection + travel buffer · earliest slot wins.",
  outcome: "Tentative invite · awaiting confirm",
  steps: [],
};

export const SCENARIOS: Scenario[] = [
  leadTimeRisk,
  budgetDrop,
  styleDrift,
  reraAging,
  memoryPromote,
  // ── Foyr-Manage-parity additions (new agents) ──
  emailDraft,
  sprintReschedule,
  estimateUpdate,
  siteVisit,
];

export const DEFAULT_SCENARIO_ID = "lead-time-risk";
